import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
  Activity, UserPlus, CreditCard, ArrowDownCircle, Mail,
  Loader2, RefreshCw, Clock
} from 'lucide-react';
import { ExportListButtons } from './ExportListButtons';
import { SearchBar, Pagination, FilterSelect } from './ListControls';

const TYPE_CONFIG = {
  registration: { icon: UserPlus, color: 'bg-[#00E5FF]/20 text-[#00E5FF]', label: 'Registration' },
  payment: { icon: CreditCard, color: 'bg-[#00E676]/20 text-[#00E676]', label: 'Payment' },
  withdrawal: { icon: ArrowDownCircle, color: 'bg-[#FFEA00]/20 text-[#FFEA00]', label: 'Withdrawal' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-PH', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

export function AdminAuditLogsPage() {
  const { api } = useAuth();
  const [auditLogs, setAuditLogs] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('activity');
  const [auditTotal, setAuditTotal] = useState(0);
  const [emailTotal, setEmailTotal] = useState(0);
  const [auditSkip, setAuditSkip] = useState(0);
  const [emailSkip, setEmailSkip] = useState(0);
  const [auditLimit, setAuditLimit] = useState(20);
  const [emailLimit, setEmailLimit] = useState(20);
  const [auditSearch, setAuditSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [retentionDays, setRetentionDays] = useState(60);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let auditUrl = `/api/admin/audit-logs?skip=${auditSkip}&limit=${auditLimit}`;
      if (auditSearch) auditUrl += `&search=${encodeURIComponent(auditSearch)}`;
      let emailUrl = `/api/admin/email-logs?skip=${emailSkip}&limit=${emailLimit}`;
      if (emailSearch) emailUrl += `&search=${encodeURIComponent(emailSearch)}`;

      const [auditRes, emailRes] = await Promise.all([
        api.get(auditUrl),
        api.get(emailUrl)
      ]);
      setAuditLogs(auditRes.data.logs || []);
      setAuditTotal(auditRes.data.total || auditRes.data.logs?.length || 0);
      setEmailLogs(emailRes.data.logs || []);
      setEmailTotal(emailRes.data.total || emailRes.data.logs?.length || 0);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  }, [api, auditSkip, auditLimit, auditSearch, emailSkip, emailLimit, emailSearch]);

  // Fetch the live retention window so the banner reflects admin config.
  useEffect(() => {
    let cancelled = false;
    api.get('/api/admin/audit-retention/config')
      .then(({ data }) => { if (!cancelled) setRetentionDays(data.days || 60); })
      .catch(() => { /* non-fatal — banner shows fallback */ });
    return () => { cancelled = true; };
  }, [api]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setAuditSkip(0); }, [auditSearch, auditLimit]);
  useEffect(() => { setEmailSkip(0); }, [emailSearch, emailLimit]);

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="audit-logs-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Audit Logs</h1>
            <p className="text-[#A0B4CC] mt-1">Monitor platform activity and email communications</p>
          </div>
          <Button onClick={fetchLogs} variant="ghost" className="text-[#00E5FF]" data-testid="refresh-logs-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <ExportListButtons api={api} resource="audit_logs" filename="audit_logs" />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#041A3B] border border-white/10 mb-6">
            <TabsTrigger value="activity" className="data-[state=active]:bg-[#00E5FF]/10 data-[state=active]:text-[#00E5FF]">
              <Activity className="w-4 h-4 mr-2" /> Activity Log
            </TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-[#00E5FF]/10 data-[state=active]:text-[#00E5FF]">
              <Mail className="w-4 h-4 mr-2" /> Email Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activity">
            <div className="space-y-4">
              <SearchBar value={auditSearch} onChange={setAuditSearch} placeholder="Search audit logs..." />
              <div className="text-[#5C7599] text-xs px-1 -mb-2" data-testid="audit-retention-notice">
                Retention policy: audit logs older than <strong className="text-white">{retentionDays} days</strong> are automatically pruned. (Configurable in Admin Settings → Maintenance.)
              </div>
              <Card className="bg-[#041A3B] border-white/10">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" /></div>
                  ) : auditLogs.length === 0 ? (
                    <div className="py-12 text-center"><Activity className="w-12 h-12 text-[#5C7599] mx-auto mb-4" /><p className="text-[#A0B4CC]">No activity logs</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10">
                            <TableHead className="text-[#A0B4CC] w-12"></TableHead>
                            <TableHead className="text-[#A0B4CC]">Type</TableHead>
                            <TableHead className="text-[#A0B4CC]">Who</TableHead>
                            <TableHead className="text-[#A0B4CC]">Description</TableHead>
                            <TableHead className="text-[#A0B4CC] text-right">Timestamp</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {auditLogs.map((log, i) => {
                            const config = TYPE_CONFIG[log.type] || { icon: Activity, color: 'bg-[#5C7599]/20 text-[#5C7599]', label: log.type || log.action || 'System' };
                            const IconComp = config.icon;
                            const actorEmail = log.actor_email || log.admin_email || 'system';
                            const actorName = log.actor_name || '';
                            const actorRole = log.actor_role || '';
                            return (
                              <TableRow key={log.id || i} className="border-white/5 hover:bg-white/5">
                                <TableCell>
                                  <div className={`w-8 h-8 rounded-lg ${config.color.split(' ')[0]} flex items-center justify-center`}>
                                    <IconComp className={`w-4 h-4 ${config.color.split(' ')[1]}`} />
                                  </div>
                                </TableCell>
                                <TableCell><Badge className={`${config.color} border-0 capitalize`}>{config.label}</Badge></TableCell>
                                <TableCell className="text-white whitespace-nowrap">
                                  <div className="flex flex-col leading-tight" data-testid={`audit-actor-${i}`}>
                                    {actorName && <span className="text-sm">{actorName}</span>}
                                    <span className={`text-xs ${actorName ? 'text-[#A0B4CC]' : 'text-white'}`}>{actorEmail}</span>
                                    {actorRole && actorRole !== 'system' && (
                                      <span className="text-[10px] uppercase tracking-wide text-[#5C7599]">{actorRole}</span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-white">{log.description || log.action || '-'}</TableCell>
                                <TableCell className="text-[#5C7599] text-right whitespace-nowrap">
                                  <div className="flex items-center justify-end gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatDate(log.timestamp || log.created_at)}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
              <Pagination skip={auditSkip} limit={auditLimit} total={auditTotal} onPageChange={setAuditSkip} onLimitChange={(v) => { setAuditLimit(v); setAuditSkip(0); }} />
            </div>
          </TabsContent>

          <TabsContent value="emails">
            <div className="space-y-4">
              <SearchBar value={emailSearch} onChange={setEmailSearch} placeholder="Search emails..." />
              <Card className="bg-[#041A3B] border-white/10">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" /></div>
                  ) : emailLogs.length === 0 ? (
                    <div className="py-12 text-center"><Mail className="w-12 h-12 text-[#5C7599] mx-auto mb-4" /><p className="text-[#A0B4CC]">No emails sent yet</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10">
                            <TableHead className="text-[#A0B4CC]">To</TableHead>
                            <TableHead className="text-[#A0B4CC]">Subject</TableHead>
                            <TableHead className="text-[#A0B4CC]">Status</TableHead>
                            <TableHead className="text-[#A0B4CC] text-right">Sent At</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {emailLogs.map((email, i) => (
                            <TableRow key={email.id || i} className="border-white/5 hover:bg-white/5">
                              <TableCell className="text-white font-medium">{email.to}</TableCell>
                              <TableCell className="text-[#A0B4CC]">{email.subject}</TableCell>
                              <TableCell>
                                <Badge className={email.status === 'sent_mock' ? 'bg-[#FFEA00]/20 text-[#FFEA00] border-0' : ['failed', 'bounced', 'blocked', 'suppressed'].includes(email.status) ? 'bg-[#FF5252]/20 text-[#FF5252] border-0' : 'bg-[#00E676]/20 text-[#00E676] border-0'}>
                                  {email.status === 'sent_mock' ? 'Mocked' : email.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-[#5C7599] text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1"><Clock className="w-3 h-3" />{formatDate(email.created_at)}</div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
              <Pagination skip={emailSkip} limit={emailLimit} total={emailTotal} onPageChange={setEmailSkip} onLimitChange={(v) => { setEmailLimit(v); setEmailSkip(0); }} />
              <div className="p-3 rounded-lg bg-[#FFEA00]/5 border border-[#FFEA00]/20">
                <p className="text-[#FFEA00] text-xs flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email sending is in mock mode. Configure Resend or SendGrid for production.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminSidebarLayout>
  );
}
